'use client';

// Рендер содержимого карточки шага (MINIMAL-PRODUCT В5 «один в один» с ChatPlace,
// приказ владельца 30.07): схема читается БЕЗ модалок — на карточке видно всё, что
// увидит человек: плашка текста, зона картинки, кнопки-пилюли отдельными рядами,
// ряды условий/действий, задержка. Эталон — data/chatplace/ui-recon/21-cards-empty.png
// и 23-card-closeup-2x.png: содержимое сообщения лежит в светлой группе, под ней —
// пунктирные «добавить…».
//
// Извлечение — чистый @scriptera/ui/scenario/step-content.ts; здесь только вид и i18n.
// Используется ОБОИМИ видами: холст (scenario-flow) и лента (scenario-vertical) —
// близнецов-вёрсток не заводим.
//
// Этап «Правка шага» (В5-ОДИН-В-ОДИН): содержимое карточки КЛИКАБЕЛЬНО — тап по элементу
// открывает правку ИМЕННО его (текст → мини-шторка текста, кнопка → шторка кнопки,
// пунктирные «добавить…» → добавление на месте), как в ChatPlace. Обработчик приходит
// провайдером (ScenarioCardEditProvider из scenario-schema-panel): на витрине и публичном
// снимке его нет — там всё остаётся неклика­бельным текстом, как было.
import { createContext, Fragment, useContext, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type {
  ChatKindId,
  StepAttachment,
  StepContentData,
  StepMessage,
  StepRow,
  StepRowIcon,
} from '@scriptera/ui/scenario';
import { condBranchTone } from '@scriptera/ui/scenario';
import { KSegment } from '@scriptera/ui/kit';
import { usePersistentData } from '@/lib/use-persistent-data';
import { IconProducts } from './icons';

// ── Товары для карточки «Оплата» ─────────────────────────────────────────────────
// Имя/цена товара живут НЕ в каноне, а в разделе «Продукты» — резолвим по общему кэшу
// cabinet:products. Провайдер монтирует ТОЛЬКО кабинет (scenario-schema-panel):
// на публичном снимке /s/[token] зритель анонимен, а usePersistentData на 401 уводит
// на /login — там фетча быть не должно; без провайдера карточка пишет общую подпись.
interface ProductLite {
  id: string;
  name: string;
  priceRub: string;
}
const ProductsCtx = createContext<ProductLite[] | null>(null);

export function ScenarioProductsProvider({ children }: { children: ReactNode }) {
  const { data } = usePersistentData<{ ok: boolean; products: ProductLite[] }>(
    'cabinet:products',
    '/api/cabinet/products',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  return <ProductsCtx.Provider value={data?.products ?? null}>{children}</ProductsCtx.Provider>;
}

// ── Статистика на карточках (К10, эталон ЧП 23/11) ───────────────────────────────
// Счётчик людей в шапке карточки и «Конверсия в клик · N%» под кнопкой. Данные —
// РЕАЛЬНЫЕ: людей на шагах считает крон по журналу запусков, нажатия — приёмник
// вебхуков. Нет данных у шага (сценарий не запускали, шаг не срабатывал, кнопка такая,
// что Телеграм про её нажатие молчит) — НЕТ И ЦИФРЫ: пусто, а не «0%» враньём.
interface StepStat {
  people: number;
  /** Подпись кнопки → нажатия и признак точности счёта (см. useButtonCtr). */
  buttons: Record<string, { clicks: number; exact: boolean }>;
  /** Люди по выходам шага (T6, CTR веток): номер выхода строкой → сколько людей ушло
   *  этим выходом. Пишет живой узел-счётчик (BOT-GUARD-PLAN T1); нет данных — нет поля. */
  outs?: Record<string, number>;
}
const StatsCtx = createContext<Record<string, StepStat> | null>(null);

/** Включить статистику на карточках (только редактор кабинета: чужому счёт не отдаём). */
export function ScenarioStatsProvider({ draftId, children }: { draftId: string; children: ReactNode }) {
  // Черновика ещё нет (сценарий собирают с нуля) — спрашивать нечего.
  const ready = /^[0-9a-f-]{36}$/i.test(draftId);
  const { data } = usePersistentData<{ ok: boolean; stats: { steps: Record<string, StepStat> } }>(
    `cabinet:scenario-stats:${draftId}`,
    ready ? `/api/cabinet/scenarios/drafts/${draftId}/stats` : '',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  return <StatsCtx.Provider value={data?.stats?.steps ?? null}>{children}</StatsCtx.Provider>;
}

/** Сколько людей прошло через шаг. null — счёта нет (карточка молчит). */
export function useStepPeople(nodeId: string | null | undefined): number | null {
  const stats = useContext(StatsCtx);
  if (!stats || !nodeId) return null;
  const people = stats[nodeId]?.people;
  return typeof people === 'number' && people > 0 ? people : null;
}

/**
 * Счётчик людей в шапке карточки (обе схемы — холст и лента). Ничего не посчитано —
 * ничего и не рисуем.
 */
export function StepPeople({ nodeId }: { nodeId: string | null | undefined }) {
  const t = useTranslations('cabinet.scenario');
  const people = useStepPeople(nodeId);
  if (people === null) return null;
  return (
    <span className="scn-cstat" title={t('statPeopleHint')}>
      <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
      {people}
    </span>
  );
}

/**
 * CTR ветки (T6, образец ChatPlace): люди выхода ÷ люди шага. null — считать нечего:
 * статистики нет, через шаг никто не прошёл или этим выходом никто не ушёл (нулей и
 * «0%» не рисуем — как у счётчика людей). Узел и выход отмечаются одним проходом
 * счётчика, поэтому больше 100% не бывает; min — страховка от устаревших строк.
 */
function useOutCtr(
  nodeId: string | null | undefined,
  out: number,
): { people: number; pct: number } | null {
  const stats = useContext(StatsCtx);
  if (!stats || !nodeId) return null;
  const step = stats[nodeId];
  if (!step || step.people <= 0) return null;
  const n = step.outs?.[String(out)];
  if (typeof n !== 'number' || n <= 0) return null;
  return { people: n, pct: Math.min(100, Math.round((n / step.people) * 100)) };
}

/**
 * Число+процент ветки мелко у её выхода (T6, вид ChatPlace): «N · P%» приглушённым
 * нейтральным, как соседние метаданные. Ничего не посчитано — не рисуется вовсе,
 * карточка без статистики выглядит как раньше.
 */
export function BranchCtr({ nodeId, out }: { nodeId: string | null | undefined; out: number }) {
  const t = useTranslations('cabinet.scenario');
  const ctr = useOutCtr(nodeId, out);
  if (ctr === null) return null;
  return (
    <span className="scn-coutc" title={t('statOutHint')}>
      {t('statOut', { n: ctr.people, pct: ctr.pct })}
    </span>
  );
}

/**
 * Конверсия кнопки в клик, % + насколько цифре можно верить. null — считать нечего
 * (см. шапку блока). `exact: false` — обычная кнопка клавиатуры: она присылает текст со
 * своей подписью, и от того же слова, набранного руками, он неотличим; на коротких
 * подписях процент завышается, поэтому карточка честно помечает его приблизительным.
 */
function useButtonCtr(
  nodeId: string | null | undefined,
  label: string,
): { pct: number; exact: boolean } | null {
  const stats = useContext(StatsCtx);
  if (!stats || !nodeId) return null;
  const step = stats[nodeId];
  if (!step || step.people <= 0) return null;
  const btn = step.buttons[label];
  if (!btn) return null;
  return { pct: Math.round((btn.clicks / step.people) * 100), exact: btn.exact };
}

// ── Правка прямо на карточке (В5 «один в один», механика ЧП) ─────────────────────
// Что именно тапнули. Дальше решает use-draft-editor: текст — мини-шторка редактора
// текста (скрин ЧП 14), кнопка — шторка кнопки (15), пунктир — добавление на месте,
// ряд условия/действия — правая шторка шага с этим рядом.
// id — шаг СТОПКИ, к которому относится тап (карточка сообщения склеена из соседних
// шагов отправки, chat-merge.ts). Не задан — сама карточка (шаг, по которому тапнули).
export type CardEditTarget =
  | { t: 'text'; id?: string }
  | { t: 'button'; index: number; id?: string }
  | { t: 'addButton'; id?: string }
  | { t: 'addContent' }
  /** Тап по вложению: выбрать файл (или заменить уже загруженный). */
  | { t: 'attach'; id?: string }
  /** Крестик у куска содержимого: убрать его из стопки. */
  | { t: 'delContent'; id?: string }
  /**
   * Плашка условия/действия. Настройка открывается ИМЕННО этой плашки (владелец 12.08):
   * id — шаг ряда действия (у карточки-стопки каждый ряд свой шаг), rowId — у карточки
   * условия это id первого условия ПИЛЮЛИ (У12). Номер оставлен запасным путём для
   * старых карточек без этих меток.
   * condId (У16) — id САМОГО условия внутри пилюли: тап по строке группы открывает
   * настройку этого условия, а не всей группы.
   */
  | { t: 'row'; index: number; id?: string; rowId?: string; condId?: string }
  /** Пунктир «Добавить условие/действие». pill (У16) — номер пилюли, В КОТОРУЮ кладём
   *  условие (кнопка внутри пузыря группы); не задан — заводим новую пилюлю. */
  | { t: 'addRow'; pill?: number }
  /**
   * Переключатель «Все условия / Любое из условий» в шапке пузыря группы (У16): выбор
   * щёлкает НА МЕСТЕ, ничего не открывая. index/rowId — метка пилюли, any — новое
   * значение.
   */
  | { t: 'condAny'; index: number; rowId?: string; any: boolean };

type CardEditFn = (nodeId: string, target: CardEditTarget) => void;
const CardEditCtx = createContext<CardEditFn | null>(null);

/** Включить правку с карточек (только редактор кабинета). */
export function ScenarioCardEditProvider({ onEdit, children }: { onEdit: CardEditFn; children: ReactNode }) {
  return <CardEditCtx.Provider value={onEdit}>{children}</CardEditCtx.Provider>;
}

/**
 * ВЫКЛЮЧИТЬ правку с карточки для КУСКА дерева (дефект №3 владельца, 25.08.2026, дословно:
 * «в любое место повтора куда бы ни нажал — открывается шторка»).
 *
 * Что было не так: у карточки-ПОВТОРА шага, живущего в шторке, открытие шторки висело на
 * рамке карточки, а её начинка (пузырь текста, кнопки, капсулы условий) оставалась
 * кликабельной по канону В5 «содержимое карточки кликабельно» — тап по тексту гасил
 * всплытие (`useTap` → stopPropagation) и открывал ПРАВКУ сообщения вместо шторки.
 * Теперь повтор — одна цель целиком: правки внутри него нет, и любой тап доходит до рамки.
 * Механика простая и без исключений в самих элементах: правка включается КОНТЕКСТОМ, и
 * пустое значение делает их обычными span'ами (см. useCardEdit/useTap/Tap ниже) — ни
 * кнопок, ни role="button", ни гашения всплытия.
 * Полная карточка ВНУТРИ шторки и все шаги её ленты правятся как обычно: там контекст свой.
 */
export function ScenarioCardEditOff({ children }: { children: ReactNode }) {
  return <CardEditCtx.Provider value={null}>{children}</CardEditCtx.Provider>;
}

/**
 * ПЕРЕНОС ОБСТАНОВКИ ЛЕНТЫ В ШТОРКУ (владелец 25.08.2026: «шаг с несколькими входами
 * живёт в шторке, и это не превью, а рабочий вид»).
 *
 * Зачем: шторку рисует drawer, а он висит НАД страницей — его содержимое стоит в дереве
 * React рядом с кабинетом, а не внутри страницы сценария. Значит три провайдера, которые
 * страница включает вокруг ленты (товары для карточки «Оплата», статистика шагов, правка
 * прямо с карточки), до ленты в шторке не доходят: контекст читается по МЕСТУ отрисовки.
 * Поэтому лента снимает их значения у себя (`useScenarioSurround`) и включает те же три
 * провайдера заново внутри шторки (`ScenarioSurround`) — без копий данных и без второго
 * запроса на сервер: значения ровно те же, что у ленты на странице.
 */
export interface ScenarioSurroundValue {
  products: ProductLite[] | null;
  stats: Record<string, StepStat> | null;
  cardEdit: CardEditFn | null;
}

/** Снять обстановку в том месте, где лента стоит на странице. */
export function useScenarioSurround(): ScenarioSurroundValue {
  return {
    products: useContext(ProductsCtx),
    stats: useContext(StatsCtx),
    cardEdit: useContext(CardEditCtx),
  };
}

/** Включить снятую обстановку вокруг ленты в шторке. */
export function ScenarioSurround({
  value,
  children,
}: {
  value: ScenarioSurroundValue;
  children: ReactNode;
}) {
  return (
    <ProductsCtx.Provider value={value.products}>
      <StatsCtx.Provider value={value.stats}>
        <CardEditCtx.Provider value={value.cardEdit}>{children}</CardEditCtx.Provider>
      </StatsCtx.Provider>
    </ProductsCtx.Provider>
  );
}

/** Правка этой карточки включена (редактор кабинета) — иначе null (витрина, снимок). */
function useCardEdit(nodeId: string | null | undefined): ((target: CardEditTarget) => void) | null {
  const edit = useContext(CardEditCtx);
  if (!edit || !nodeId) return null;
  return (target) => edit(nodeId, target);
}

/** Тап по элементу карточки: не всплывает до карточки (иначе откроется общий редактор). */
function useTap(nodeId: string | null | undefined): ((target: CardEditTarget) => (e: MouseEvent) => void) | null {
  const edit = useCardEdit(nodeId);
  if (!edit) return null;
  return (target) => (e) => {
    e.stopPropagation();
    edit(target);
  };
}

/**
 * Элемент содержимого: кликабельный (кнопка) — когда правка включена, иначе прежний span.
 * `nodrag` — чтобы тап по элементу на холсте не начинал перетаскивание карточки,
 * `nopan` — чтобы он же не начинал ТАСКАТЬ ХОЛСТ под пальцем: у нажимаемого места
 * холста стоят оба класса (У13; тем же лечился тулбар карточки, см. card-tools.tsx).
 */
function Tap({
  onTap,
  className,
  title,
  children,
}: {
  onTap: ((e: MouseEvent) => void) | null;
  className: string;
  title?: string;
  children: ReactNode;
}) {
  if (!onTap) return <span className={className}>{children}</span>;
  return (
    <button type="button" className={`${className} scn-ctap nodrag nopan`} onClick={onTap} title={title}>
      {children}
    </button>
  );
}

// ── Кусочки ──────────────────────────────────────────────────────────────────────

/** Ключи подписей вложения по виду (общие для плашки, зоны загрузки и меню контента). */
const ATTACH_KEYS: Record<StepAttachment, { word: string; add: string }> = {
  image: { word: 'contentAttachImage', add: 'attachAddImage' },
  video: { word: 'contentAttachVideo', add: 'attachAddVideo' },
  audio: { word: 'contentAttachAudio', add: 'attachAddAudio' },
  file: { word: 'contentAttachFile', add: 'attachAddFile' },
};

/** Глиф вида вложения — нужен и меню «Добавить контент» (chat-card-edit). */
export function AttachGlyph({ kind, size = 13 }: { kind: StepAttachment; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {kind === 'image' ? (
          <>
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="9" cy="9" r="2" />
            <path d="m3 17 5-5 4 4 3-3 6 6" />
          </>
        ) : kind === 'video' ? (
          <>
            <rect x="2" y="5" width="14" height="14" rx="3" />
            <path d="m16 10 6-3v10l-6-3" />
          </>
        ) : kind === 'audio' ? (
          <>
            <path d="M9 18V6l10-2v12" />
            <circle cx="6.5" cy="18" r="2.5" />
            <circle cx="16.5" cy="16" r="2.5" />
          </>
        ) : (
          <>
            <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5z" />
            <path d="M14 2v5h5" />
          </>
        )}
    </svg>
  );
}

/** Крестик у куска содержимого (эталон ЧП 22 — кружок с крестом у зоны файла). */
function RemoveDot({ onTap, label }: { onTap: (e: MouseEvent) => void; label: string }) {
  return (
    <button type="button" className="scn-cdel nodrag nopan" onClick={onTap} title={label} aria-label={label}>
      <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    </button>
  );
}

/**
 * Вложение сообщения на карточке (эталон ЧП 22):
 *  • файл не выбран — пунктирная зона «Добавить изображение · png, jpeg, gif; до 5 МБ»;
 *  • картинка загружена — миниатюрой;
 *  • видео/аудио/документ — плашкой «тип · имя файла».
 * Тап открывает выбор файла, крестик убирает этот кусок содержимого.
 */
function AttachView({
  kind,
  url,
  name,
  onTap,
  onRemove,
}: {
  kind: StepAttachment;
  url: string | null;
  name: string | null;
  onTap: ((e: MouseEvent) => void) | null;
  onRemove: ((e: MouseEvent) => void) | null;
}) {
  const t = useTranslations('cabinet.scenario');
  const keys = ATTACH_KEYS[kind];
  const del = onRemove ? <RemoveDot onTap={onRemove} label={t('attachRemove')} /> : null;
  if (url && kind === 'image') {
    return (
      <span className="scn-cthumb">
        <Tap className="scn-cthumb-in" onTap={onTap} title={t(keys.add)}>
          {/* eslint-disable-next-line @next/next/no-img-element -- файл из нашего хранилища, размер заранее неизвестен */}
          <img decoding="async" src={url} alt={name ?? t(keys.word)} loading="lazy" />
        </Tap>
        {del}
      </span>
    );
  }
  // «Добавить …» зовём только там, где правка включена (редактор кабинета); на витрине и
  // публичном снимке зритель ничего не добавляет — там просто вид содержимого.
  const word = url || !onTap ? t(keys.word) : t(keys.add);
  // Подзаголовок. Файл есть — его имя. Файла НЕТ — говорим это прямо и везде (претензия
  // владельца 31.07: «видел безымянную иконку»): раньше в редакторе тут стоял список
  // форматов, который читался как содержимое сообщения. Точные форматы и предел размера
  // живут в редакторе сообщения — они СВОИ У КАЖДОГО КАНАЛА, а карточка канала не знает.
  const sub = url ? (name ?? t('attachFileNoName')) : t('attachFileNone');
  return (
    <span className={`scn-cmedia${url ? ' filled' : ''}`}>
      <Tap className="scn-cmedia-in" onTap={onTap} title={t(keys.add)}>
        <AttachGlyph kind={kind} />
        <span className="scn-cmedia-word">{word}</span>
        <span className="scn-cmedia-sub">{sub}</span>
      </Tap>
      {del}
    </span>
  );
}

// Кнопки сообщения — КАЖДАЯ отдельным рядом-пилюлей во всю ширину (карточка ЧП: подпись
// по центру, под ней мелкая приписка). Раньше были мелкие чипы в строку — на карточке ЧП
// такого нет.
function Buttons({
  labels,
  tap,
  id,
  statId,
  btnPort,
}: {
  labels: string[];
  tap: ReturnType<typeof useTap>;
  /** Шаг стопки, которому принадлежат кнопки (правка идёт в него). */
  id?: string;
  /** Шаг, на котором считаются нажатия этих кнопок (К10): стопка — свой шаг. */
  statId?: string | null;
  /** К34: порт-точка ветки у кнопки (холст). Лента ничего не рисует. */
  btnPort?: ButtonPort;
}) {
  if (labels.length === 0) return null;
  return (
    <>
      {labels.map((b, i) => {
        const pill = (
          <Tap
            className="scn-cbtn"
            onTap={tap ? tap({ t: 'button', index: i, ...(id ? { id } : {}) }) : null}
          >
            {b}
            <ButtonCtr nodeId={statId ?? null} label={b} />
          </Tap>
        );
        // К34: у кнопки может висеть ВЕТКА — тогда рядом с пилюлей живёт порт-точка (и «+»
        // на свободном выходе). Порт кладём в обёртку, а НЕ внутрь пилюли: тап по кнопке
        // открывает её правку, служебной точке холста там не место.
        const port = btnPort?.(statId ?? null, i);
        if (!port) return <Fragment key={i}>{pill}</Fragment>;
        return (
          <span key={i} className="scn-cbtnp">
            {pill}
            {port}
          </span>
        );
      })}
    </>
  );
}

/**
 * Мелкая приписка под кнопкой: «Конверсия в клик · N%» (эталон ЧП 23). У обычной кнопки
 * клавиатуры счёт приблизительный — пишем «≈» и объясняем почему в подсказке (врать
 * точной цифрой там, где её нет, нельзя).
 */
function ButtonCtr({ nodeId, label }: { nodeId: string | null; label: string }) {
  const t = useTranslations('cabinet.scenario');
  const ctr = useButtonCtr(nodeId, label);
  if (ctr === null) return null;
  if (ctr.exact) return <span className="scn-cbtn-ctr">{t('statCtr', { pct: ctr.pct })}</span>;
  return (
    <span className="scn-cbtn-ctr approx" title={t('statCtrApproxHint')}>
      {t('statCtrApprox', { pct: ctr.pct })}
    </span>
  );
}

/** Один кусок содержимого сообщения: вложение, текст, кнопки — в порядке шагов ЧП. */
function MessagePart({
  part,
  tap,
  id,
  statId,
  removable,
  btnPort,
}: {
  part: StepMessage;
  tap: ReturnType<typeof useTap>;
  id?: string;
  /** Шаг, на котором считаются нажатия кнопок этого куска (К10). */
  statId?: string | null;
  /** Крестик «убрать этот кусок» (в редакторе кабинета). */
  removable: boolean;
  /** К34: порт ветки у кнопки (холст). */
  btnPort?: ButtonPort;
}) {
  const t = useTranslations('cabinet.scenario');
  const at = id ? { id } : {};
  return (
    <>
      {part.attachment ? (
        <AttachView
          kind={part.attachment}
          url={part.attachUrl}
          name={part.attachName}
          onTap={tap ? tap({ t: 'attach', ...at }) : null}
          onRemove={tap && removable ? tap({ t: 'delContent', ...at }) : null}
        />
      ) : null}
      {part.text ? (
        <Tap className="scn-ctext" onTap={tap ? tap({ t: 'text', ...at }) : null} title={t('chatMsgText')}>
          {part.text}
        </Tap>
      ) : null}
      <Buttons labels={part.buttons} tap={tap} {...at} statId={statId ?? null} {...(btnPort ? { btnPort } : {})} />
    </>
  );
}

// Пунктирная кнопка «добавить…» ПРЯМО НА КАРТОЧКЕ (пункт сверки 1: схема читается без
// модалок, добавление видно на месте). Тап открывает добавление ИМЕННО этого (кнопку /
// контент / условие / действие) — как в ChatPlace.
function AddSlot({
  label,
  pill,
  inner,
  onTap,
}: {
  label: string;
  pill?: boolean;
  /** Кнопка живёт ВНУТРИ пузыря (У16: «Добавить условие» в группе) — мельче и без полей. */
  inner?: boolean;
  onTap: ((e: MouseEvent) => void) | null;
}) {
  return (
    <Tap className={`scn-cadd${pill ? ' pill' : ''}${inner ? ' in' : ''}`} onTap={onTap}>
      {label}
    </Tap>
  );
}

function ProductRow({
  productId,
  custom,
  onTap,
}: {
  productId: string;
  /** Разовая оплата (Ф3.2): имя и цена из самого шага, каталог не при чём. */
  custom: { name: string; priceRub: string } | null;
  onTap: ((e: MouseEvent) => void) | null;
}) {
  const t = useTranslations('cabinet.scenario');
  const products = useContext(ProductsCtx);
  const found = !custom && productId ? products?.find((p) => p.id === productId) : undefined;
  // Разовая оплата — имя/цена прямо из шага; товар не выбран → подсказка «выбрать»;
  // выбран, но кэша нет (публичный снимок) — общая подпись «товар из раздела „Продукты“»,
  // цен не выдумываем.
  const name = custom
    ? custom.name || t('contentProductNone')
    : found
      ? found.name
      : productId
        ? t('contentProductGeneric')
        : t('contentProductNone');
  const price = custom ? custom.priceRub : found?.priceRub ?? '';
  return (
    <Tap className="scn-cprod" onTap={onTap}>
      <span className="scn-cprod-ico" aria-hidden>
        <IconProducts width={16} height={16} />
      </span>
      <span className="scn-cprod-text">
        <span className="scn-cprod-name">{name}</span>
        {price ? <span className="scn-cprod-price">{t('contentProductPrice', { price })}</span> : null}
      </span>
    </Tap>
  );
}

// Ряды-плашки «что делает шаг» (В5.3, вид ЧП): метка, переменная, задержка, карточка
// человека, проверка подписки, проверка оплаты. Глифы линейные (канон no-emoji).
const ROW_GLYPHS: Record<StepRowIcon, ReactNode> = {
  tag: (
    <>
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
      <path d="M7.5 7.5h.01" />
    </>
  ),
  var: (
    <>
      <path d="M8 4c-2.5 2.4-2.5 13.2 0 16M16 4c2.5 2.4 2.5 13.2 0 16" />
      <path d="M10 9.5h4M10 14.5h4" />
    </>
  ),
  delay: (
    <>
      <path d="M7 3h10M7 21h10" />
      <path d="M8 3c0 4 4 5.5 4 9s-4 5-4 9M16 3c0 4-4 5.5-4 9s4 5 4 9" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="9" cy="11" r="2.3" />
      <path d="M5.5 17c.8-1.7 2-2.5 3.5-2.5s2.7.8 3.5 2.5M15.5 10h3.5M15.5 13.5h3.5" />
    </>
  ),
  subscribe: (
    <>
      <circle cx="10" cy="8.5" r="3.5" />
      <path d="M4 20c0-3.3 2.7-5.5 6-5.5 1.2 0 2.3.3 3.2.8" />
      <path d="m15 17.5 2 2 4-4.5" />
    </>
  ),
  cond: (
    <>
      <path d="M4 5h16l-6.5 7.5V20l-3-2v-5.5L4 5z" />
    </>
  ),
  pay: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19M6 14.5h4" />
    </>
  ),
  // Категория «Время» каталога условий (К4, эталон ЧП 17): часы, календарь, день недели.
  time: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.8" />
    </>
  ),
  date: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8 3.5V6M16 3.5V6" />
    </>
  ),
  weekday: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8 3.5V6M16 3.5V6M7.5 14h4M7.5 17h9" />
    </>
  ),
  // К6: запрос к ИИ (искры), запрос к сервису (обмен стрелками), запрос номера (трубка),
  // действие в чате (три точки набора). Линейные, как остальные (канон no-emoji).
  ai: (
    <>
      <path d="M12 3.5l1.7 4.3 4.3 1.7-4.3 1.7-1.7 4.3-1.7-4.3L6 11.5l4.3-1.7z" />
      <path d="M18 15.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
    </>
  ),
  api: (
    <>
      <path d="M4 8.5h13M13.5 5l3.5 3.5-3.5 3.5" />
      <path d="M20 15.5H7M10.5 12 7 15.5l3.5 3.5" />
    </>
  ),
  phone: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </>
  ),
  typing: (
    <>
      <path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5h-9L7 20v-4.5H4A1.5 1.5 0 0 1 2.5 14V7A1.5 1.5 0 0 1 4 5.5Z" />
      <path d="M8 10.5h.01M12 10.5h.01M16 10.5h.01" />
    </>
  ),
  // К37-Z5: ответ на комментарий — стрелка «ответить».
  reply: (
    <>
      <path d="m9 6-6 5.5L9 17v-3.5c5 0 8 1.6 9.5 5 .4-6.5-3-10-9.5-10.5V6Z" />
    </>
  ),
  // Ф5.1: «Поставить реакцию» — сердечко. Линейный SVG (канон no-emoji); сам эмодзи
  // реакции — значение ряда (контент клиента).
  react: (
    <>
      <path d="M12 20.5S3.5 15.4 3.5 9.6C3.5 6.8 5.7 4.5 8.4 4.5c1.5 0 2.8.7 3.6 1.9.8-1.2 2.1-1.9 3.6-1.9 2.7 0 4.9 2.3 4.9 5.1 0 5.8-8.5 10.9-8.5 10.9Z" />
    </>
  ),
  // Э3: «Изменить сообщение» — карандаш (правка уже сказанного).
  edit: (
    <>
      <path d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16v4Z" />
      <path d="m13.5 6.5 4 4" />
    </>
  ),
  // Э3: «Сохранить сообщение» — закладка на пузыре.
  bookmark: (
    <>
      <path d="M6.5 3.5h11a1 1 0 0 1 1 1v16l-6.5-4-6.5 4v-16a1 1 0 0 1 1-1Z" />
    </>
  ),
};

/** Глиф ряда (тот же, что на карточке) — нужен и каталогам «+ Условие»/«+ Действие». */
export function RowGlyph({ icon, size = 13 }: { icon: StepRowIcon; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {ROW_GLYPHS[icon]}
    </svg>
  );
}

/**
 * Заголовок плашки (У19, владелец 14.08): всегда наше слово по ключу — имя ВИДА условия
 * или действия, ровно то же, что стоит пунктом каталога и шапкой модалки настройки. Всё,
 * что выбрал человек (имя запомненного значения, метки, время), живёт в ЗНАЧЕНИИ.
 */
function rowLabel(t: ReturnType<typeof useTranslations>, row: StepRow): string {
  return t(row.labelKey);
}

/**
 * ЗНАЧЕНИЕ плашки серой строкой под заголовком (У18 — один формат на все плашки: условия,
 * действия, задержка). Три источника: наше слово по ключу («печатает…»), имя продукта из
 * раздела «Продукты» по id и обычный текст (метка, время, значение). Приставка `valuePrefix`
 * («без» у меток, которых быть не должно) встаёт перед текстом. Нечего показать — строки
 * нет вовсе.
 */
function RowValue({ row }: { row: StepRow }) {
  const t = useTranslations('cabinet.scenario');
  const products = useContext(ProductsCtx);
  if (row.valueKey) return <span className="scn-crow-v">{t(row.valueKey)}</span>;
  // Кэша товаров нет (публичный снимок, витрина) — молчим: выдумывать имя нельзя.
  const product = row.valueProductId
    ? (products?.find((p) => p.id === row.valueProductId)?.name ?? null)
    : null;
  const body = product ?? (row.value ? (row.unitKey ? `${row.value} ${t(row.unitKey)}` : row.value) : null);
  const text = row.valuePrefix ? [row.valuePrefix, body].filter(Boolean).join(' ') : body;
  return text ? <span className="scn-crow-v">{text}</span> : null;
}

/** Строка условия внутри пузыря пилюли: значок, имя, значение. Тап — настройка ЭТОГО условия. */
function CondLine({
  row,
  className,
  onTap,
}: {
  row: StepRow;
  className: string;
  onTap: ((e: MouseEvent) => void) | null;
}) {
  const t = useTranslations('cabinet.scenario');
  return (
    <Tap className={className} onTap={onTap}>
      <span className="scn-crow-ico" aria-hidden>
        <RowGlyph icon={row.icon} />
      </span>
      <span className="scn-crow-k">{rowLabel(t, row)}</span>
      <RowValue row={row} />
    </Tap>
  );
}

/**
 * ПУЗЫРЬ ПИЛЮЛИ карточки условия (У16, заказ владельца 13.08): группа правится ПРЯМО НА
 * КАРТОЧКЕ — сверху две пилюли «Хватит одного / Нужны все» (щёлкают на месте, видны
 * только при двух и больше условиях), ниже сами условия строками (тап открывает
 * настройку именно этого условия), внизу «Добавить условие» в ЭТУ группу.
 * Пузырь целиком НЕ нажимается: нажимаются его части (У13 — рамка только у нажимаемого).
 *
 * На витрине и публичном снимке (tap === null) пузырь остаётся прежним текстом: там ни
 * пилюль режима, ни «добавить».
 */
function CondPill({
  row,
  index,
  tap,
  edit,
  rowPort,
  total,
  outStatId,
  condTone,
}: {
  row: StepRow;
  index: number;
  tap: ReturnType<typeof useTap>;
  /** Правка без события мыши (выбор в переключателе группы). */
  edit: ((target: CardEditTarget) => void) | null;
  rowPort?: RowPort;
  total: number;
  /** T6: шаг, по чьим выходам считается CTR ветки этой пилюли (пилюля i = выход i). */
  outStatId?: string | null;
  /** С30: номер цвета ПЕРВОГО выхода этого условия — старт его куска сквозной очереди. */
  condTone?: number;
}) {
  const t = useTranslations('cabinet.scenario');
  const mark = { index, ...(row.rowId ? { rowId: row.rowId } : {}) };
  const open = (condId?: string) =>
    tap ? tap({ t: 'row', ...mark, ...(condId ? { condId } : {}) }) : null;
  // Пустая пилюля («Условие не задано») — тап по ней открывает каталог этой же группы:
  // настраивать в ней нечего, пока условия нет.
  const empty = !row.sub && row.bad;
  // С30: цвет этой ветки = место в СКВОЗНОЙ очереди схемы (старт условия + номер пилюли).
  // Счёт один на все виды — его же зовут лента и холст, иначе цвета разъедутся.
  const tone = condBranchTone(condTone ?? 1, index);
  return (
    // Ц6 (владелец 17.08): цвет ветки по её месту в очереди. Ставим его прямо на капсулу —
    // от него красятся и кружок с цифрой, и отвод от него, и порт-точка холста внутри этого
    // же ряда. Один и тот же код рисует карточку в ЛЕНТЕ и на ХОЛСТЕ, поэтому цвета в обоих
    // видах совпадают (владелец: «человек переключается — пусть видит те же цвета для тех
    // же вещей»).
    // С28 (владелец 23.08, IMG_2486): очередь ОДНА И ШЕСТИЦВЕТНАЯ — 1 бирюзовый, 2 жёлтый,
    // 3 фуксия, 4 зелёный, 5 оранжевый, 6 синий, седьмая группа снова первая. Раньше очередей
    // было две (заливки по шести, оси схемы по трём) и они расходились: цифра в кружке
    // оказывалась не той семьи, что заливка под ней. Теперь у группы ОДНА база --cbase-N, и
    // от неё считаются и заливка, и кружок, и шнурок, и значки строк (рецепт семей — в
    // cabinet.css, блок «СЕМЬИ ЛИНИЙ»).
    // С30 (владелец 25.08): очередь СКВОЗНАЯ НА ВСЮ СХЕМУ, а не своя у каждого условия.
    // Раньше здесь стоял номер пилюли ((index % 6) + 1), и каждая развилка схемы начинала
    // раскраску заново с бирюзы — все условия выглядели одинаково. Теперь цвет = старт
    // этого условия в общей очереди (condTone, считает ядро cond-marks.ts) плюс номер
    // пилюли; «не подошло» цвета не получает и очередь не двигает.
    <div
      className={`scn-crow plate scn-cpill${row.bad ? ' bad' : ''}`}
      style={
        {
          '--cbase': `var(--cbase-${tone})`,
          '--wire-c': `var(--wire-${tone})`,
          '--cgfill': `var(--cgfill-${tone})`,
        } as CSSProperties
      }
    >
      {/* Режим группы — только у группы из нескольких условий (владелец 13.08): у одного
          условия выбирать не из чего, и лишнее слово на карточке ни к чему.
          С26 (владелец 23.08, IMG_2481): слова тумблера — «и» / «или», ровно те же, что
          стоят СВЯЗКАМИ между строками группы ниже; прежние «Хватит одного / Нужны все»
          сняты. Обе подписи по-прежнему на виду (владелец 16.08: у настоящего тумблера
          видна была только текущая, и она читалась как название настройки), подсвечена
          действующая, тап по соседней переключает. Контрол — из набора (KSegment
          size='sm'), своей разметки пилюль здесь нет. Дефолт группы — «и» (any=false,
          комбинатор 'and' в каноне).
          С29 (владелец 24.08, IMG_2489): фирменного розового в тумблере больше нет —
          проп `tone` включает выделение ТОНОМ СВОЕЙ ГРУППЫ (базу и подложку набор берёт
          переменными обёртки --seg-tone/--seg-surf, их ставит .scn-cpill-head в
          cabinet.css). Невыбранное слово — тихий серый.
          Клик гасим на обойме: карточка ленты по своему onClick открывает настройку
          шага, а тап по её внутренности этого делать не должен (тот же приём, что у Tap). */}
      {row.sub && row.any !== undefined && edit ? (
        <span className="scn-cpill-head" onClick={(e) => e.stopPropagation()}>
          <KSegment
            size="sm"
            tone
            className="nodrag nopan"
            ariaLabel={t('chatCondMatches')}
            value={row.any ? 'any' : 'all'}
            options={[
              { value: 'all', label: t('condSayAnd') },
              { value: 'any', label: t('condSayOr') },
            ]}
            onChange={(v) => edit({ t: 'condAny', ...mark, any: v === 'any' })}
          />
        </span>
      ) : null}
      {row.sub ? (
        <span className="scn-csub">
          {/* С27 (владелец 23.08, IMG_2484): слов-связок «и»/«или» между капсулами строк
              БОЛЬШЕ НЕТ — они дублировали тумблер и шумели. Режим группы показывает и
              переключает ТОЛЬКО тумблер «и | или» выше. Ключи condSayAnd/condSayOr не
              трогаем: их по-прежнему зовёт подпись условия (cond-note). */}
          {row.sub.map((s, j) => (
            <CondLine key={s.rowId || j} row={s} className="scn-csub-row" onTap={open(s.rowId)} />
          ))}
        </span>
      ) : (
        <CondLine
          row={row}
          className="scn-cpill-row"
          onTap={empty && tap ? tap({ t: 'addRow', pill: index }) : open()}
        />
      )}
      {tap ? (
        <AddSlot label={t('chatCondAdd')} inner onTap={tap({ t: 'addRow', pill: index })} />
      ) : null}
      {/* T6: CTR ветки пилюли — люди её выхода ÷ люди шага, мелко у правого края
          (рядом с кружком-цифрой выхода). Без статистики не рисуется вовсе. */}
      {outStatId ? <BranchCtr nodeId={outStatId} out={index} /> : null}
      {rowPort?.(index, total)}
    </div>
  );
}

function RowsView({
  rows,
  rowPort,
  tap,
  edit,
  outStatId,
  condTone,
}: {
  rows: StepRow[];
  rowPort?: RowPort;
  tap: ReturnType<typeof useTap>;
  edit: ((target: CardEditTarget) => void) | null;
  /** T6: шаг для CTR веток пилюль (см. StepContentView). */
  outStatId?: string | null;
  /** С30: старт этого условия в сквозной очереди цветов (см. StepContentView). */
  condTone?: number;
}) {
  const t = useTranslations('cabinet.scenario');
  return (
    <>
      {rows.map((r, i) => {
        // Пилюля карточки «Условие» (У16) — своя вёрстка: группа правится на карточке.
        if (typeof r.pill === 'number') {
          return (
            <CondPill
              key={i}
              row={r}
              index={r.pill}
              tap={tap}
              edit={edit}
              {...(rowPort ? { rowPort } : {})}
              total={rows.length}
              {...(outStatId ? { outStatId } : {})}
              {...(condTone ? { condTone } : {})}
            />
          );
        }
        // Ряд остаётся <div> (внутри живёт порт-точка холста — Handle в кнопку не вкладываем):
        // кликабельность даём role="button" + onClick, тап не всплывает до карточки.
        const onTap = tap?.({
          t: 'row',
          index: i,
          ...(r.nodeId ? { id: r.nodeId } : {}),
          ...(r.rowId ? { rowId: r.rowId } : {}),
        });
        return (
          <div
            key={i}
            // bad — ряд-недоделка: та же плашка, только красная.
            className={`scn-crow plate${r.bad ? ' bad' : ''}${onTap ? ' scn-crow-tap nodrag nopan' : ''}`}
            {...(onTap
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: onTap,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onTap(e as unknown as MouseEvent);
                    }
                  },
                }
              : {})}
          >
            <span className="scn-crow-ico" aria-hidden>
              <RowGlyph icon={r.icon} />
            </span>
            <span className="scn-crow-k">{rowLabel(t, r)}</span>
            {/* Значение — серой строкой под заголовком (У18): данные клиента (метка,
                адрес, товар) или НАШЕ слово по ключу («печатает…»). */}
            <RowValue row={r} />
            {rowPort?.(i, rows.length)}
          </div>
        );
      })}
    </>
  );
}

// ── Содержимое целиком ───────────────────────────────────────────────────────────

/** Порт-точка у ряда содержимого (холст): чем рисовать выход ряда i из total. */
export type RowPort = (index: number, total: number) => ReactNode;

/**
 * К34: порт-точка у КНОПКИ сообщения (холст) — ветка выходит прямо из кнопки, как в ЧП.
 * stepId — шаг, которому кнопка принадлежит (в карточке-стопке кнопки у своего куска),
 * index — её место в этом шаге. null — у этой кнопки ветки нет.
 */
export type ButtonPort = (stepId: string | null | undefined, index: number) => ReactNode;

/**
 * Содержимое карточки шага (В5 «один в один»). null-содержимое сюда не передают.
 *
 * chatKind — вид карточки: по нему выбирается подпись пунктирной кнопки «добавить…»
 * (сообщение → «Добавить контент», условие → «Добавить условие», действие →
 * «Добавить действие»), как на карточках ЧП. Не задан (шаг вне чат-набора) —
 * пунктирных кнопок нет: у таких шагов на карточке нечего добавлять на месте.
 * rowPort — порт-точка у ряда (холст рисует Handle, лента ничего).
 * nodeId — id шага: с ним элементы содержимого становятся кликабельными (правка на
 * месте, механика ЧП); без него — прежний неклика­бельный вид (витрина, снимок).
 */
export function StepContentView({
  content,
  chatKind,
  rowPort,
  btnPort,
  nodeId,
  outStatId,
  condTone,
}: {
  content: StepContentData;
  chatKind?: ChatKindId | null;
  rowPort?: RowPort;
  /** К34: порт ветки у кнопки (холст); лента его не передаёт. */
  btnPort?: ButtonPort;
  nodeId?: string | null;
  /**
   * T6: шаг, по чьим выходам считать CTR веток пилюль условия. Передаётся ТОЛЬКО когда
   * вызывающий сверил счёт «пилюля i = выход i» (canvas — condPorts, лента — condPills):
   * у чужой развилки или error-выходов соответствие не обещано, и цифра была бы враньём.
   */
  outStatId?: string | null;
  /**
   * С30 (владелец 25.08.2026): номер цвета ПЕРВОГО выхода этого условия — старт его куска
   * СКВОЗНОЙ очереди цветов схемы (считает ядро cond-marks.ts, тот же номер носит фигурка
   * блока). Отсюда красятся капсулы условий: цвет пилюли = старт + её номер. Не задан
   * (витрина, снимок, шаг вне схемы) — очередь начинается с первого цвета, как раньше.
   */
  condTone?: number;
}) {
  const t = useTranslations('cabinet.scenario');
  // Правку с карточки включаем только для шагов чат-набора: у шагов «глубокой двери»
  // содержимое не про диалог, там прежний путь — тап по карточке → полная форма узла.
  const tap = useTap(chatKind ? nodeId : null);
  // У16: выбор в переключателе группы (Ж5 — тумблер) правит канон напрямую, той же
  // дорогой, что и тапы.
  const edit = useCardEdit(chatKind ? nodeId : null);
  switch (content.kind) {
    case 'message':
    case 'stack': {
      // Стопка содержимого (В5-ПРИЁМКА К1, эталон ЧП 22): куски идут в порядке шагов
      // сценария — картинка, текст, ещё текст; у каждого свой шаг канона, поэтому правка
      // тапом уходит именно в него. Одиночное сообщение — та же разметка без id.
      const parts =
        content.kind === 'stack'
          ? content.items.map((it) => ({ part: it as StepMessage, id: it.nodeId }))
          : [{ part: content as StepMessage, id: undefined }];
      // «Добавить быстрый ответ» дописывает кнопку к ПОСЛЕДНЕМУ сообщению стопки — под
      // ним кнопки и появятся в чате.
      const lastId = parts[parts.length - 1]?.id;
      return (
        <>
          {/* Светлая группа вокруг содержимого сообщения — как на карточке ЧП. */}
          <span className="scn-cgroup">
            {parts.map(({ part, id }, i) => (
              <MessagePart
                key={id ?? i}
                part={part}
                tap={tap}
                {...(id ? { id } : {})}
                statId={id ?? nodeId ?? null}
                removable={content.kind === 'stack'}
                {...(btnPort ? { btnPort } : {})}
              />
            ))}
            {chatKind === 'message' ? (
              <AddSlot
                label={t('chatMsgBtnAdd')}
                pill
                onTap={tap ? tap({ t: 'addButton', ...(lastId ? { id: lastId } : {}) }) : null}
              />
            ) : null}
          </span>
          {chatKind === 'message' ? (
            <AddSlot label={t('cardAddContent')} onTap={tap ? tap({ t: 'addContent' }) : null} />
          ) : null}
        </>
      );
    }
    case 'form':
      return (
        <>
          <span className="scn-cgroup">
            {content.title ? <span className="scn-ctext">{content.title}</span> : null}
            {content.fields.map((f, i) => (
              <div key={i} className="scn-crow">
                <span className="scn-crow-k">{f}</span>
                <span className="scn-crow-v">{t('contentFormField')}</span>
              </div>
            ))}
            <Buttons labels={content.buttons} tap={null} />
          </span>
        </>
      );
    case 'rows':
      return (
        <>
          <RowsView
            rows={content.rows}
            rowPort={rowPort}
            tap={tap}
            edit={edit}
            {...(outStatId ? { outStatId } : {})}
            {...(condTone ? { condTone } : {})}
          />
          {chatKind === 'condition' ? (
            <AddSlot label={t('chatCondAdd')} onTap={tap ? tap({ t: 'addRow' }) : null} />
          ) : null}
          {chatKind === 'action' ? (
            <AddSlot label={t('cardAddAction')} onTap={tap ? tap({ t: 'addRow' }) : null} />
          ) : null}
        </>
      );
    case 'product':
      return (
        <ProductRow
          productId={content.productId}
          custom={content.custom}
          onTap={tap ? tap({ t: 'row', index: 0 }) : null}
        />
      );
    case 'crmLead':
      return (
        <>
          {content.title ? (
            <div className="scn-crow">
              <span className="scn-crow-k">{t('contentCrmTitle')}</span>
              <span className="scn-crow-v">{content.title}</span>
            </div>
          ) : null}
          {content.attachProfile ? (
            <>
              <div className="scn-crow">
                <span className="scn-crow-k">{t('contentCrmName')}</span>
                <span className="scn-crow-v">{t('contentCrmFromCard')}</span>
              </div>
              <div className="scn-crow">
                <span className="scn-crow-k">{t('contentCrmPhone')}</span>
                <span className="scn-crow-v">{t('contentCrmFromCard')}</span>
              </div>
            </>
          ) : null}
        </>
      );
  }
}
